import React, { useState, useEffect, useMemo } from 'react';
import { useNotifications } from '../../../contexts/NotificationContext';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  LinearProgress,
  Tooltip,
  IconButton,
  Fade,
  Skeleton,
  useTheme,
  alpha,
  Stack
} from '@mui/material';
import {
  PeopleAlt as PeopleAltIcon,
  Mic as MicIcon,
  Description as DescriptionIcon,
  AccessTime as AccessTimeIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Analytics as AnalyticsIcon,
  Speed as SpeedIcon,
  AutoAwesome as AutoAwesomeIcon,
  AccountCircle as AccountCircleIcon
} from '@mui/icons-material';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell,
  Area,
  AreaChart
} from 'recharts';
import { getApplicationStats, invalidateStatsCache } from '../../../firebase/services/statisticsService';

// Palette de couleurs moderne 2025
const COLORS = {
  primary: '#6366f1',
  secondary: '#8b5cf6',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#3b82f6',
  surface: '#ffffff'
};

// Composant de métrique moderne avec glassmorphism
const ModernMetricCard = ({ icon, title, value, suffix, trend, color = COLORS.primary, isLoading = false }) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.05)} 100%)`,
        backdropFilter: 'blur(20px)',
        border: `1px solid ${alpha(color, 0.2)}`,
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 12px 40px ${alpha(color, 0.15)}`,
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: `linear-gradient(90deg, ${color} 0%, ${alpha(color, 0.6)} 100%)`,
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ 
            background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.8)} 100%)`,
            borderRadius: '12px', 
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 8px 32px ${alpha(color, 0.3)}`
          }}>
            {React.cloneElement(icon, { 
              sx: { color: 'white', fontSize: 28 }
            })}
          </Box>
          
          {trend && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              bgcolor: trend > 0 ? alpha(COLORS.success, 0.1) : alpha(COLORS.error, 0.1),
              borderRadius: '8px',
              px: 1,
              py: 0.5
            }}>
              <TrendingUpIcon 
                sx={{ 
                  fontSize: 16, 
                  color: trend > 0 ? COLORS.success : COLORS.error,
                  transform: trend < 0 ? 'rotate(180deg)' : 'none',
                  mr: 0.5
                }} 
              />
              <Typography variant="caption" sx={{ 
                color: trend > 0 ? COLORS.success : COLORS.error,
                fontWeight: 600
              }}>
                {Math.abs(trend)}%
              </Typography>
            </Box>
          )}
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
          {title}
        </Typography>
        
        {isLoading ? (
          <Skeleton variant="text" width="60%" height={48} />
        ) : (
          <Typography variant="h4" component="div" sx={{ 
            fontWeight: 700,
            background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'flex',
            alignItems: 'baseline',
            gap: 0.5
          }}>
            {value !== undefined && value !== null ? value : '-'}
            {value !== undefined && value !== null && suffix && (
              <Typography variant="body1" sx={{ 
                color: 'text.secondary',
                fontWeight: 500,
                fontSize: '0.75rem'
              }}>
                {suffix}
              </Typography>
            )}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

// Composant de graphique moderne
const ModernChartCard = ({ title, children, action, isLoading = false }) => {
  const theme = useTheme();
  
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
        backdropFilter: 'blur(20px)',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: `linear-gradient(90deg, transparent 0%, ${alpha(COLORS.primary, 0.3)} 50%, transparent 100%)`,
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {title}
        </Typography>
        {action}
      </Box>
      
      {isLoading ? (
        <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={40} sx={{ color: COLORS.primary }} />
        </Box>
      ) : (
        children
      )}
    </Paper>
  );
};

// Composant principal StatisticsPanel
const StatisticsPanel = () => {
  const { addSystemNotification } = useNotifications();
  const theme = useTheme();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Charger les statistiques
  const loadStats = async (forceRefresh = false) => {
    try {
      setLoading(!stats); // Ne pas montrer le loading si on a déjà des données
      if (forceRefresh) {
        setRefreshing(true);
        invalidateStatsCache();
      }
      
      const data = await getApplicationStats();
      setStats(data);
      setError(null);
    } catch (err) {
      addSystemNotification('Erreur lors du chargement des statistiques:', 'error');
      setError('Impossible de charger les statistiques. Veuillez réessayer.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  // Fonction de rafraîchissement
  const handleRefresh = () => {
    loadStats(true);
  };

  // Formater la durée
  const formatDuration = (seconds) => {
    if (!seconds || seconds <= 0) return '-';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    }
    return `${minutes}min`;
  };

  // Données pour le graphique en secteurs des services
  const serviceData = useMemo(() => {
    if (!stats?.transcriptionsByService) return [];
    
    return [
      { name: 'OpenAI', value: stats.transcriptionsByService.openAI, color: COLORS.primary },
      { name: 'AssemblyAI', value: stats.transcriptionsByService.assemblyAI, color: COLORS.secondary }
    ].filter(item => item.value > 0);
  }, [stats]);

  // Données pour le graphique des utilisateurs actifs
  const topUsersData = useMemo(() => {
    if (!stats?.topUsers) return [];
    
    return stats.topUsers.map((user, index) => ({
      name: user.name?.length > 15 ? `${user.name.slice(0, 15)}...` : user.name,
      fullName: user.name,
      transcriptions: user.transcriptions,
      meetings: user.meetings,
      totalActivity: user.totalActivity,
      color: [COLORS.primary, COLORS.secondary, COLORS.success, COLORS.warning, COLORS.info][index % 5]
    }));
  }, [stats]);

  if (loading && !stats) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Statistiques d'utilisation
        </Typography>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item}>
              <Skeleton variant="rounded" height={120} />
            </Grid>
          ))}
          {[1, 2].map((item) => (
            <Grid item xs={12} md={6} key={item}>
              <Skeleton variant="rounded" height={300} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        sx={{ 
          my: 3,
          borderRadius: 2,
          background: `linear-gradient(135deg, ${alpha(COLORS.error, 0.1)} 0%, ${alpha(COLORS.error, 0.05)} 100%)`,
          border: `1px solid ${alpha(COLORS.error, 0.2)}`
        }}
        action={
          <IconButton color="inherit" size="small" onClick={handleRefresh}>
            <RefreshIcon />
          </IconButton>
        }
      >
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* En-tête avec action de rafraîchissement */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h2" sx={{ 
            fontWeight: 700,
            background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1
          }}>
            Tableau de bord analytique
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Vue d'ensemble des performances de la plateforme
          </Typography>
        </Box>
        
        <Tooltip title="Actualiser les données">
          <IconButton 
            onClick={handleRefresh} 
            disabled={refreshing}
            sx={{
              background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
              color: 'white',
              '&:hover': {
                background: `linear-gradient(135deg, ${alpha(COLORS.primary, 0.8)} 0%, ${alpha(COLORS.secondary, 0.8)} 100%)`,
              }
            }}
          >
            <RefreshIcon sx={{ 
              animation: refreshing ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Indicateur de mise à jour en cours */}
      {refreshing && (
        <Fade in={refreshing}>
          <Alert 
            severity="info" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${alpha(COLORS.info, 0.1)} 0%, ${alpha(COLORS.info, 0.05)} 100%)`,
              border: `1px solid ${alpha(COLORS.info, 0.2)}`
            }}
          >
            Actualisation des données en cours...
          </Alert>
        </Fade>
      )}
      
      {/* Cartes de métriques principales */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <ModernMetricCard 
            icon={<PeopleAltIcon />} 
            title="Utilisateurs actifs" 
            value={stats?.totalUsers}
            color={COLORS.primary}
            isLoading={refreshing}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ModernMetricCard 
            icon={<MicIcon />} 
            title="Réunions totales" 
            value={stats?.totalMeetings}
            color={COLORS.success}
            isLoading={refreshing}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ModernMetricCard 
            icon={<DescriptionIcon />} 
            title="Transcriptions" 
            value={stats?.totalTranscriptions}
            color={COLORS.warning}
            isLoading={refreshing}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ModernMetricCard 
            icon={<AccessTimeIcon />} 
            title="Durée moyenne" 
            value={formatDuration(stats?.averageAudioDurationInSeconds)}
            color={COLORS.secondary}
            isLoading={refreshing}
          />
        </Grid>
      </Grid>

      {/* Métriques avancées */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <ModernMetricCard 
            icon={<CheckCircleIcon />} 
            title="Taux de succès" 
            value={stats?.transcriptionSuccessRate}
            suffix="%"
            color={COLORS.success}
            isLoading={refreshing}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ModernMetricCard 
            icon={<AutoAwesomeIcon />} 
            title="Résumés générés" 
            value={stats?.totalSummaries}
            color={COLORS.info}
            isLoading={refreshing}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ModernMetricCard 
            icon={<SpeedIcon />} 
            title="Moy. transcriptions/user" 
            value={stats?.metrics?.avgTranscriptionsPerUser}
            color={COLORS.warning}
            isLoading={refreshing}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ModernMetricCard 
            icon={<AnalyticsIcon />} 
            title="Taux de résumés" 
            value={Math.round((stats?.metrics?.summaryGenerationRate || 0) * 100)}
            suffix="%"
            color={COLORS.secondary}
            isLoading={refreshing}
          />
        </Grid>
      </Grid>
      
      {/* Graphiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Activité mensuelle */}
        <Grid item xs={12} lg={8}>
          <ModernChartCard title="Évolution mensuelle" isLoading={refreshing}>
            <Box sx={{ height: 350 }}>
              {stats?.monthlyActivity && stats.monthlyActivity.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats.monthlyActivity} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <defs>
                      <linearGradient id="meetingsGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3}/>
                        <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="transcriptionsGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.secondary} stopOpacity={0.3}/>
                        <stop offset="95%" stopColor={COLORS.secondary} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                    <XAxis 
                      dataKey="month" 
                      tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis 
                      tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <RechartsTooltip 
                      contentStyle={{
                        backgroundColor: alpha(theme.palette.background.paper, 0.9),
                        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                        borderRadius: '12px',
                        backdropFilter: 'blur(20px)'
                      }}
                    />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="meetings" 
                      name="Réunions" 
                      stroke={COLORS.primary}
                      strokeWidth={2}
                      fill="url(#meetingsGradient)"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="transcriptions" 
                      name="Transcriptions" 
                      stroke={COLORS.secondary}
                      strokeWidth={2}
                      fill="url(#transcriptionsGradient)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  height: '100%',
                  color: 'text.secondary'
                }}>
                  <Typography variant="body1">
                    Aucune donnée d'activité disponible
                  </Typography>
                </Box>
              )}
            </Box>
          </ModernChartCard>
        </Grid>
        
        {/* Services de transcription */}
        <Grid item xs={12} lg={4}>
          <ModernChartCard title="Répartition par service" isLoading={refreshing}>
            <Box sx={{ height: 350 }}>
              {serviceData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={serviceData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {serviceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      contentStyle={{
                        backgroundColor: alpha(theme.palette.background.paper, 0.9),
                        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                        borderRadius: '12px',
                        backdropFilter: 'blur(20px)'
                      }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  height: '100%',
                  color: 'text.secondary'
                }}>
                  <Typography variant="body1">
                    Aucune donnée de service disponible
                  </Typography>
                </Box>
              )}
            </Box>
          </ModernChartCard>
        </Grid>
      </Grid>

      {/* Utilisateurs les plus actifs et informations système */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={6}>
          <ModernChartCard title="Top utilisateurs actifs" isLoading={refreshing}>
            <Box sx={{ height: 300 }}>
              {topUsersData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={topUsersData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                    <XAxis 
                      dataKey="name"
                      tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis 
                      tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <RechartsTooltip 
                      contentStyle={{
                        backgroundColor: alpha(theme.palette.background.paper, 0.9),
                        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                        borderRadius: '12px',
                        backdropFilter: 'blur(20px)'
                      }}
                      formatter={(value, name) => [
                        value,
                        name === 'totalActivity' ? 'Activité totale' : name
                      ]}
                    />
                    <Bar 
                      dataKey="totalActivity" 
                      name="Activité totale"
                      radius={[6, 6, 0, 0]}
                      fill={COLORS.primary}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  height: '100%',
                  color: 'text.secondary'
                }}>
                  <Typography variant="body1">
                    Aucun utilisateur actif trouvé
                  </Typography>
                </Box>
              )}
            </Box>
          </ModernChartCard>
        </Grid>
        
        {/* Informations système */}
        <Grid item xs={12} lg={6}>
          <ModernChartCard title="Informations système" isLoading={refreshing}>
            <Stack spacing={2}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                background: alpha(COLORS.primary, 0.05),
                border: `1px solid ${alpha(COLORS.primary, 0.1)}`
              }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Dernière mise à jour
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {stats?.generatedAt ? new Date(stats.generatedAt).toLocaleString('fr-FR') : '-'}
                </Typography>
              </Box>
              
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                background: alpha(COLORS.success, 0.05),
                border: `1px solid ${alpha(COLORS.success, 0.1)}`
              }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Cache expire le
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {stats?.cacheExpiry ? new Date(stats.cacheExpiry).toLocaleString('fr-FR') : '-'}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Métriques de performance
              </Typography>
              
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Taux de succès</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {stats?.transcriptionSuccessRate || 0}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={stats?.transcriptionSuccessRate || 0} 
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: alpha(COLORS.success, 0.1),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      background: `linear-gradient(90deg, ${COLORS.success} 0%, ${alpha(COLORS.success, 0.8)} 100%)`
                    }
                  }}
                />
              </Box>
            </Stack>
          </ModernChartCard>
        </Grid>
      </Grid>
      
      {/* Dernières réunions */}
      <ModernChartCard title="Activité récente" isLoading={refreshing}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Titre</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Utilisateur</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stats?.recentMeetings && stats.recentMeetings.length > 0 ? (
                stats.recentMeetings.map((meeting) => (
                  <TableRow key={meeting.id} hover sx={{ '&:hover': { bgcolor: alpha(COLORS.primary, 0.02) } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ 
                          width: 32, 
                          height: 32, 
                          bgcolor: alpha(COLORS.primary, 0.1),
                          color: COLORS.primary
                        }}>
                          <MicIcon fontSize="small" />
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {meeting.title || 'Réunion sans titre'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {meeting.createdAt ? 
                          new Date(meeting.createdAt).toLocaleString('fr-FR', { 
                            dateStyle: 'short', 
                            timeStyle: 'short' 
                          }) : '-'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 24, height: 24, bgcolor: alpha(COLORS.secondary, 0.1) }}>
                          <AccountCircleIcon fontSize="small" />
                        </Avatar>
                        <Typography variant="body2">
                          {meeting.userName || meeting.userEmail || '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={
                          meeting.status === 'completed' ? 'Terminé' :
                          meeting.status === 'processing' ? 'En cours' :
                          meeting.status === 'error' ? 'Erreur' : 
                          'En attente'
                        }
                        color={
                          meeting.status === 'completed' ? 'success' : 
                          meeting.status === 'processing' ? 'warning' : 
                          meeting.status === 'error' ? 'error' : 
                          'default'
                        }
                        size="small"
                        sx={{
                          fontWeight: 500,
                          borderRadius: 2
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      Aucune activité récente
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </ModernChartCard>
    </Box>
  );
};

export default StatisticsPanel; 